import { useConnectModal } from '@rainbow-me/rainbowkit'
import { FC, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getAddress } from 'viem'

import Button from 'components/Button'
import UnderlineTabs from 'components/Button/UnderlineTabs'
import { BaseCard } from 'components/Card/style'
import { FlexDivCol, FlexDivColCentered, FlexDivRow } from 'components/layout/flex'
import { TabPanel } from 'components/Tab'
import { Body } from 'components/Text'
import { DASHBOARD_SECTION_HEIGHT_PX, MOBILE_FOOTER_HEIGHT } from 'constants/ui'
import AssetsTable from 'sections/futures/UserInfo/AssetsTable'
import {
	CrossMarginConditionalOrdersTable,
	SmartMarginConditionalOrdersTable,
} from 'sections/futures/UserInfo/ConditionalOrdersTable'
import PositionsTable from 'sections/futures/UserInfo/PositionsTable'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectConditionalOrdersCount } from 'state/futures/selectors'
import {
	selectActiveCrossMarginPositionsCount,
	selectV3MarginChartData,
} from 'state/futures/snxPerpsV3/selectors'
import { PerpsProvider } from 'state/futures/types'
import { useAppSelector } from 'state/hooks'
import { useLeaderboardData } from 'state/stats/hooks'
import { selectLeaderboard } from 'state/stats/selectors'
import { selectWallet } from 'state/wallet/selectors'
import media from 'styles/media'

import { INITIAL_TRADER_PROFILE } from './Leaderboard/constants'
import AccountBalance from './Overview/AccountBalance'
import { OverviewTab } from './Overview/constants'
import MarketWatcher from './Overview/MarketWatcher'
import PortfolioChart from './PortfolioChart'
import TraderProfileHeader from './shared/TraderProfileHeader'

const Overview: FC = () => {
	const { t } = useTranslation()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const conditionalOrdersCount = useAppSelector(selectConditionalOrdersCount)
	const leaderboardData = useAppSelector(selectLeaderboard)
	const crossPositionsCount = useAppSelector(selectActiveCrossMarginPositionsCount)
	const wallet = useAppSelector(selectWallet)
	const v3MarginSnapshots = useAppSelector(selectV3MarginChartData)
	const { openConnectModal } = useConnectModal()
	const [activeTab, setActiveTab] = useState<OverviewTab>(OverviewTab.POSITIONS)
	const [maskAsSensitive, toggleMaskAsSensitive] = useReducer((o) => !o, false)

	useLeaderboardData()

	const walletProfile = useMemo(() => {
		const user = leaderboardData.all.filter(({ trader }) => getAddress(trader) === wallet)
		return user.length > 0 ? user[0] : INITIAL_TRADER_PROFILE
	}, [leaderboardData.all, wallet])

	const { balanceChange, initialTotal, finalTotal } = useMemo(() => {
		if (!v3MarginSnapshots || v3MarginSnapshots.length === 0) {
			return { balanceChange: 0, initialTotal: 0, finalTotal: 0 }
		}
		const dataLength = v3MarginSnapshots.length
		const initialTotal = v3MarginSnapshots[0].total
		const finalIndex = dataLength < 24 ? dataLength - 1 : dataLength - 24
		const finalTotal = v3MarginSnapshots[finalIndex].total
		const balanceChange = finalTotal - initialTotal

		return { balanceChange, initialTotal, finalTotal }
	}, [v3MarginSnapshots])

	const balanceChangePercent = useMemo(() => {
		if (!balanceChange || initialTotal === 0 || finalTotal === 0) return 0
		return balanceChange / (v3MarginSnapshots.length < 24 ? initialTotal : finalTotal)
	}, [balanceChange, finalTotal, initialTotal, v3MarginSnapshots.length])

	const userTabs = useMemo(
		() => [
			{
				name: OverviewTab.POSITIONS,
				label: t('dashboard.overview.tabs.positions'),
				badge: crossPositionsCount,
				active: activeTab === OverviewTab.POSITIONS,
				onClick: (): void => {
					setActiveTab(OverviewTab.POSITIONS)
				},
				component: <PositionsTable />,
			},
			{
				name: OverviewTab.ORDERS,
				label: t('dashboard.overview.tabs.orders'),
				active: activeTab === OverviewTab.ORDERS,
				badge: conditionalOrdersCount,
				onClick: (): void => {
					setActiveTab(OverviewTab.ORDERS)
				},
				component:
					perpsProvider === PerpsProvider.SNX_V3 ? (
						<CrossMarginConditionalOrdersTable />
					) : (
						<SmartMarginConditionalOrdersTable />
					),
			},
			{
				name: OverviewTab.BALANCES,
				label: t('dashboard.overview.tabs.balances'),
				active: activeTab === OverviewTab.BALANCES,
				onClick: (): void => {
					setActiveTab(OverviewTab.BALANCES)
				},
				component: <AssetsTable />,
			},
		],
		[perpsProvider, activeTab, conditionalOrdersCount, crossPositionsCount, t]
	)

	return (
		<OverviewContainer rowGap="25px">
			<TraderProfileHeader {...walletProfile} />
			<CardContainer columnGap="15px">
				<AccountBalance
					maskAsSensitive={maskAsSensitive}
					toggleMaskAsSensitive={toggleMaskAsSensitive}
					balanceChange={balanceChange}
					balanceChangePercent={balanceChangePercent}
				/>
				<ChartContainer $noPadding>
					{wallet ? (
						<PortfolioChart />
					) : (
						<FlexDivColCentered rowGap="15px">
							<Body color="secondary">{t('dashboard.overview.no-wallet-connected')}</Body>
							<Button variant="solid-yellow" size="small" onClick={openConnectModal}>
								{t('futures.market.trade.trade-balance.connect-wallet-button')}
							</Button>
						</FlexDivColCentered>
					)}
				</ChartContainer>
				<MarketsContainer>
					<MarketWatcher />
				</MarketsContainer>
			</CardContainer>
			<FlexDivCol rowGap="20px">
				<TabButtonsContainer>
					<UnderlineTabs options={userTabs} />
				</TabButtonsContainer>
				<TableContainer>
					{userTabs.map(({ name, component }) => (
						<CollapsibleTabPanel
							key={name}
							collapsed={true}
							name={name}
							activeTab={activeTab}
							fullHeight
						>
							{component}
						</CollapsibleTabPanel>
					))}
				</TableContainer>
			</FlexDivCol>
		</OverviewContainer>
	)
}

const ChartContainer = styled(BaseCard)`
	flex: 8;
	border-radius: 8px;
	min-width: 340px;
	height: ${DASHBOARD_SECTION_HEIGHT_PX};
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`

const CardContainer = styled(FlexDivRow)`
	flex-wrap: wrap;
	row-gap: 15px;
`

const OverviewContainer = styled(FlexDivCol)`
	margin-top: 25px;
	margin-bottom: 25px;
	height: 100%;
	padding: 15px;
`

const CollapsibleTabPanel = styled(TabPanel).attrs({ fade: true })<{ collapsed: boolean }>`
	display: flex;
	flex-direction: column;
	flex: ${({ collapsed }) => (collapsed ? '0' : 'auto')};
	transition: flex 0.4s ease-in-out;
`

const TableContainer = styled.div`
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-top: none;
	border-radius: 8px;
	overflow: hidden;

	${media.lessThan('lg')`
		border-top: ${(props) => props.theme.colors.selectedTheme.border};
		margin-bottom: calc(${MOBILE_FOOTER_HEIGHT} + 20px);
	`}
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;
	width: 100%;

	button {
		font-size: 13px;
	}

	> div > div {
		border-right: none;
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

const MarketsContainer = styled.div`
	${media.lessThan('xxl')`
		display: none;
	`}
`

export default Overview
